import { rem } from 'polished'
import type { Dispatch, ChangeEvent } from 'react'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import FirstTime from '../Copy'

import TextInput from 'components/Common/Form/Input/TextInput'

import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  margin-bottom: ${rem(8)};

  .error-count {
    align-self: start;
  }
`

interface NameTripProps {
  setErrorMsg: Dispatch<React.SetStateAction<string>>
  setTripName: Dispatch<React.SetStateAction<string>>
  onSubmit?: (name: string) => void
  firstTime: boolean
  tripName: string
  errorMsg: string
}

function NameTrip({
  setErrorMsg,
  setTripName,
  onSubmit,
  firstTime,
  tripName,
  errorMsg,
}: NameTripProps) {
  const intl = useIntl()
  const containerRef = useRef<HTMLDivElement>(null)

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorMsg('')
    setTripName(e.currentTarget.value)
  }

  useEffect(() => {
    // Ideally we'd just put a ref on the input itself, but it's
    // based on an old class component, so too difficult.
    const ref = containerRef.current?.querySelector('input')
    if (ref) {
      ref.focus()
    }
  }, [])

  const tripNamePlaceholder = intl.formatMessage({
    id: 'f1E9ubwfI9V0HKmAga39',
    defaultMessage: 'e.g. Bali Trip',
    description: 'Placeholder text for trip name input field in the trip planner',
  })

  const tripNameLabel = intl.formatMessage({
    id: 'pvM0qB8ik9qSMOhrvQuP',
    defaultMessage: 'Trip name',
    description: 'Label for trip name input field in the trip planner feature',
  })

  return (
    <Container ref={containerRef}>
      {firstTime && <FirstTime />}
      <Group direction="vertical" gap={4}>
        <TextInput
          placeholder={tripNamePlaceholder}
          maxLength={MAX_TRIP_NAME_CHARS}
          label={tripNameLabel}
          onChange={onInputChange}
          data-testid="modal-trip-input"
          value={tripName}
          onKeyDown={(e) => {
            if (e.code === 'Enter' && tripName.length > 0) {
              onSubmit?.(tripName)
            }
          }}
          noValidationMessage
          noValidationSpacing
          required
        />
        <Group direction="horizontal" horizontalAlign="space-between">
          <Caption as="span" variant="medium" colour="critical">
            {errorMsg}
          </Caption>
          <Caption as="span" variant="large" colour="neutral-three">
            {intl.formatMessage(
              {
                id: 'z62o7-KmOt1dDhi4gubx',
                defaultMessage: '{current}/{max}',
                description: 'Character count indicator for the trip name input field in the trip planner entry point',
              },
              { current: tripName.length, max: MAX_TRIP_NAME_CHARS },
            )}
          </Caption>
        </Group>
      </Group>
    </Container>
  )
}

export default NameTrip
